import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { apiFetch } from "@/lib/api-client";

/**
 * G.5: Live Broadcast Room — Presenter View
 * Uses LiveKit Room with full publish capabilities.
 * Presenter can: camera, mic, screen-share, see attendee count, end webinar.
 */
export default function LivePresenterPage() {
  const { webinarId } = useParams();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [livekitHost, setLivekitHost] = useState<string>("");
  const [isConnecting, setIsConnecting] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get LiveKit token
  useEffect(() => {
    if (!webinarId) return;

    async function fetchToken() {
      try {
        const data = await apiFetch<{
          token: string;
          livekitHost: string;
          room: string;
        }>(`/v1/webinars/${webinarId}/join-token`, {
          method: "POST",
          body: JSON.stringify({
            role: "presenter",
            displayName: "Presenter",
          }),
        });
        setToken(data.token);
        setLivekitHost(data.livekitHost);
        setIsConnecting(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to connect");
        setIsConnecting(false);
      }
    }

    fetchToken();
  }, [webinarId]);

  const handleEndWebinar = useCallback(async () => {
    if (!webinarId) return;
    try {
      await apiFetch(`/v1/webinars/${webinarId}/end`, { method: "POST" });
      navigate("/webinars");
    } catch {
      // Error state handled by status polling
    }
  }, [webinarId, navigate]);

  if (isConnecting) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <div className="text-center text-white">
          <div className="mb-4 text-lg">Connecting to broadcast room...</div>
          <div className="h-1 w-48 overflow-hidden rounded-full bg-gray-700">
            <div className="h-full w-1/3 animate-pulse rounded-full bg-primary" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <div className="text-center text-white">
          <p className="mb-4 text-lg">Unable to join broadcast</p>
          <p className="text-sm text-[var(--color-muted-foreground)]">{error ?? "No token received"}</p>
          <button
            onClick={() => navigate(`/webinar/${webinarId}/backstage`)}
            className="mt-4 rounded-md bg-primary px-4 py-2 text-sm"
          >
            Back to Backstage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-black">
      {/* Top bar */}
      <div className="flex items-center justify-between bg-gray-900 px-4 py-2">
        <div className="flex items-center gap-3">
          <div className="h-2 w-2 animate-pulse rounded-full bg-[var(--color-destructive)]/50" />
          <span className="text-sm font-medium text-white">LIVE</span>
        </div>
        <button
          onClick={handleEndWebinar}
          className="rounded-md bg-[var(--color-destructive)] px-4 py-1.5 text-sm font-medium text-white hover:bg-[var(--color-destructive)]"
        >
          End Webinar
        </button>
      </div>

      {/* LiveKit Room */}
      <div className="flex-1">
        <LiveKitRoom
          token={token}
          serverUrl={livekitHost}
          connect={true}
          video={true}
          audio={true}
          data-lk-theme="default"
        >
          <VideoConference />
          <RoomAudioRenderer />
        </LiveKitRoom>
      </div>
    </div>
  );
}
